import { Scale } from 'lucide-react';
import { useEffect, useState } from 'react';
import { normalizePatientWeightKg } from '../utils/patientMetrics';

interface AssessmentWeightAdjustmentFieldProps {
  value: number | null;
  baselineValue?: number | null;
  onChange: (value: number | null) => void;
  label?: string;
  helperText?: string;
}

const formatWeight = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? String(value).replace('.', ',') : '';

export default function AssessmentWeightAdjustmentField({
  value,
  baselineValue = null,
  onChange,
  label = 'Peso para esta avaliação',
  helperText = 'Ajuste local. Não altera o cadastro do paciente.',
}: AssessmentWeightAdjustmentFieldProps) {
  const [inputValue, setInputValue] = useState(formatWeight(value));

  useEffect(() => {
    setInputValue(formatWeight(value));
  }, [value]);

  const baselineLabel = baselineValue != null ? `${baselineValue.toFixed(1).replace('.', ',')} kg` : 'não informado';
  const isAdjusted = value != null && baselineValue != null ? Math.abs(value - baselineValue) > 0.01 : false;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-white p-2 text-slate-700 shadow-sm">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{label}</div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{helperText}</p>
          </div>
        </div>
        <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
          Cadastro: {baselineLabel}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="min-w-0 flex-1 text-sm text-slate-700">
          Peso usado nos cálculos e no laudo
          <div className="relative mt-1">
            <input
              type="text"
              inputMode="decimal"
              value={inputValue}
              onChange={(event) => {
                const nextValue = event.target.value;
                setInputValue(nextValue);
                if (!nextValue.trim()) {
                  onChange(null);
                  return;
                }
                const normalized = normalizePatientWeightKg(nextValue.replace(',', '.'));
                if (normalized != null) {
                  onChange(normalized);
                }
              }}
              onBlur={() => setInputValue(formatWeight(value))}
              placeholder="Ex.: 72,5"
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-12 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-500">kg</span>
          </div>
        </label>

        {baselineValue != null ? (
          <button
            type="button"
            onClick={() => onChange(baselineValue)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Usar peso do cadastro
          </button>
        ) : null}
      </div>

      {isAdjusted ? (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          O peso desta avaliação foi ajustado localmente para este teste.
        </div>
      ) : null}
    </div>
  );
}
